// External Imports
import React, { useRef } from "react";

// Internal Imports
import { Modal } from "../../../../components/core/modal/Modal";
import {
    CookiePolicyModalContainer,
    CookiePolicyModalContentContainer,
    CookiePolicyModalParagraph,
    CookiePolicyModalTitle,
    CookiePolicyModalTable,
    CookiePolicyModalTableRow,
    CookiePolicyModalTableHead,
    CookiePolicyModalTableData,
    CookiePolicyModalTableBox,
    CookiePolicyModalBtns,
    PrintableArea,
    Div,
} from "./CookiePolicyModal.styles";
import WebsiteButton from "../../../../components/core/button/WebsiteButton";
import PrintButton from "../../../../components/core/button/print/PrintButton";

// Language Context
import { useLanguage } from "../../../../core/translations/LanguageContext";

// Translations
import { CookiesContentModal } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../../../core/translations/supportedLanguages";

const TRANSLATIONS: Record<SupportedLanguage, CookiesContentModal> = {
    en: enTranslations,
    de: deTranslations,
};

/**
 * Props for the CookiePolicyModal component
 */
interface CookiePolicyModalProps {
    onRequestClose: () => void;
}

/**
 * A modal that displays a cookie policy. This contains a cookie policy for the game, with
 * a single button to close the modal.
 *
 * @param onClose - A function to call when the modal is closed
 */
export const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({
    onRequestClose,
}) => {
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];

    /**
     * Include useRef at the top of the page and create const from it to include on page section.
     * After adding this to printable section pass the same in PrintButton component
     * Then print button will print the section provided to it.
     */

    const componentRef = useRef();
    // Logic to close the modal
    const closeModal = (value:string) => {
        console.log("Popup closed");
        localStorage.setItem("cookiesSeen", "true");
        localStorage.setItem("cookiesAccepted", value);

        onRequestClose();
    };

    return (
        <Modal onRequestClose={()=>{closeModal("false")}}>
            <CookiePolicyModalContainer>
                <CookiePolicyModalContentContainer>
                    {/* @ts-ignore */}

                    <PrintableArea ref={componentRef}>
                        <CookiePolicyModalTitle>{translations.title}</CookiePolicyModalTitle>
                        <CookiePolicyModalParagraph>{translations.content}</CookiePolicyModalParagraph>
                        {translations.sections.map((section, index) => {
                            return (
                                <Div key={index}>
                                    <CookiePolicyModalTitle>{section.title}</CookiePolicyModalTitle>
                                    {section.content.map((paragraph, index)=>{return <CookiePolicyModalParagraph key={index}>{paragraph}</CookiePolicyModalParagraph>})}
                                </Div>
                            );
                        })}

                        <CookiePolicyModalTitle>{translations.tableBox.title}</CookiePolicyModalTitle>
                        <CookiePolicyModalTableBox>
                            <CookiePolicyModalTable>
                                <CookiePolicyModalTableRow>
                                    {translations.tableBox.tableHead.map((data,index)=>{return <CookiePolicyModalTableHead key={index}>{data}</CookiePolicyModalTableHead>})}
                                </CookiePolicyModalTableRow>
                                <CookiePolicyModalTableRow>
                                    {translations.tableBox.tableData.map((data,index)=>{return <CookiePolicyModalTableData key={index}>{data}</CookiePolicyModalTableData>})}
                                </CookiePolicyModalTableRow>
                            </CookiePolicyModalTable>
                        </CookiePolicyModalTableBox>
                        <CookiePolicyModalParagraph>{translations.tableBox.content}</CookiePolicyModalParagraph>
                    </PrintableArea>
                    <CookiePolicyModalBtns>
                        <WebsiteButton tag="button" onClick={()=>{closeModal("true")}}>
                            {translations.accceptAllButton}
                        </WebsiteButton>
                        <WebsiteButton tag="button" onClick={()=>{closeModal("false")}}>
                            {translations.rejectButton}
                        </WebsiteButton>
                        {/* @ts-ignore */}
                        <PrintButton printableSection={componentRef} />
                    </CookiePolicyModalBtns>
                </CookiePolicyModalContentContainer>
            </CookiePolicyModalContainer>
        </Modal>
    );
};

export default CookiePolicyModal;
